import { listUserDocuments, getDocumentUrl } from './documentService';
import type {
  HealthSummaryResult,
  AppendixFile,
  FieldValue,
  EmergencyContact,
  FamilyDoctor,
  Condition,
  Allergy,
  Medication,
  VaccineHistory,
  DiagnosticHistory,
  SurgicalHistory,
  TravelHistory,
  FamilyHistory,
  CareTeamMember,
} from '../types/healthSummary';

/**
 * Find a header index by checking common variations (case-insensitive).
 */
function findCol(headers: string[], ...candidates: string[]): number {
  const normalized = headers.map((h) => h.trim().toLowerCase());
  for (const c of candidates) {
    const idx = normalized.indexOf(c.toLowerCase());
    if (idx !== -1) return idx;
  }
  return -1;
}

function cellStr(row: any[], idx: number): string {
  if (idx < 0 || idx >= row.length) return '';
  const v = row[idx];
  if (v == null) return '';
  if (v instanceof Date) {
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, '0');
    const d = String(v.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return String(v).trim();
}

function getXLSX() {
  return (window as any).__XLSX__;
}

function getRows(sheet: any): any[][] {
  return getXLSX().utils.sheet_to_json(sheet, { header: 1 });
}

// --- Field/Value sheets (Personal Identification, Demographics, Insurance & Care Planning) ---
function parseFieldValue(sheet: any): FieldValue[] {
  const rows = getRows(sheet);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h: any) => String(h));
  const fieldIdx = findCol(headers, 'field');
  const valueIdx = findCol(headers, 'value');
  if (fieldIdx === -1 || valueIdx === -1) return [];
  return rows.slice(1)
    .filter((r) => cellStr(r, fieldIdx))
    .map((r) => ({ field: cellStr(r, fieldIdx), value: cellStr(r, valueIdx) }));
}

function parseEmergencyContacts(sheet: any): EmergencyContact[] {
  const rows = getRows(sheet);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h: any) => String(h));
  const nameIdx = findCol(headers, 'name', 'contact name');
  const relIdx = findCol(headers, 'relationship', 'relation');
  const phoneIdx = findCol(headers, 'phone', 'phone number');
  const emailIdx = findCol(headers, 'email', 'email address');
  return rows.slice(1)
    .filter((r) => cellStr(r, nameIdx))
    .map((r) => ({
      name: cellStr(r, nameIdx),
      relationship: cellStr(r, relIdx),
      phone: cellStr(r, phoneIdx),
      email: cellStr(r, emailIdx),
    }));
}

function parseFamilyDoctor(sheet: any): FamilyDoctor[] {
  const rows = getRows(sheet);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h: any) => String(h ?? ''));
  const nameIdx = findCol(headers, 'name');
  const addressIdx = findCol(headers, 'address');
  const phoneIdx = findCol(headers, 'phone');
  const faxIdx = findCol(headers, 'fax');
  // Column 0 is the role label (Family Doctor, Preferred Pharmacy, etc.)
  return rows.slice(1)
    .filter((r) => cellStr(r, 0) || cellStr(r, nameIdx))
    .map((r) => ({
      role: cellStr(r, 0),
      name: cellStr(r, nameIdx),
      address: cellStr(r, addressIdx),
      phone: cellStr(r, phoneIdx),
      fax: cellStr(r, faxIdx),
    }));
}

function parseConditions(sheet: any): Condition[] {
  const rows = getRows(sheet);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h: any) => String(h));
  const condIdx = findCol(headers, 'medical condition', 'condition');
  const dateIdx = findCol(headers, 'date of onset', 'date diagnosed', 'date');
  const readingIdx = findCol(headers, 'recent reading (if applicable)', 'recent reading');
  const commentsIdx = findCol(headers, 'other comments', 'comments', 'notes');
  return rows.slice(1)
    .filter((r) => cellStr(r, condIdx))
    .map((r) => ({
      condition: cellStr(r, condIdx),
      dateOfOnset: cellStr(r, dateIdx),
      recentReading: cellStr(r, readingIdx),
      comments: cellStr(r, commentsIdx),
    }));
}

function parseAllergies(sheet: any): Allergy[] {
  const rows = getRows(sheet);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h: any) => String(h));
  const allergenIdx = findCol(headers, 'allergen / agent', 'allergen', 'allergy');
  const reactionIdx = findCol(headers, 'reaction type', 'reaction');
  const severityIdx = findCol(headers, 'severity');
  const commentsIdx = findCol(headers, 'other comments', 'comments', 'notes');
  return rows.slice(1)
    .filter((r) => cellStr(r, allergenIdx))
    .map((r) => ({
      allergen: cellStr(r, allergenIdx),
      reactionType: cellStr(r, reactionIdx),
      severity: cellStr(r, severityIdx),
      comments: cellStr(r, commentsIdx),
    }));
}

function parseMedications(sheet: any): Medication[] {
  const rows = getRows(sheet);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h: any) => String(h));
  const nameIdx = findCol(headers, 'medication name', 'name', 'medication');
  const dosageIdx = findCol(headers, 'dosage / frequency', 'dosage', 'dose');
  const firstIdx = findCol(headers, 'date first dispensed');
  const lastIdx = findCol(headers, 'date last dispensed');
  const providerIdx = findCol(headers, 'prescribing provider', 'prescriber');

  // Merge parenthetical generic-name rows into preceding medication
  const allMeds: Medication[] = [];
  for (const r of rows.slice(1)) {
    const name = cellStr(r, nameIdx);
    if (!name) continue;
    if (name.startsWith('(') && allMeds.length > 0) {
      allMeds[allMeds.length - 1].name += ' ' + name;
    } else {
      allMeds.push({
        name,
        dosageFrequency: cellStr(r, dosageIdx),
        dateFirstDispensed: cellStr(r, firstIdx),
        dateLastDispensed: cellStr(r, lastIdx),
        prescribingProvider: cellStr(r, providerIdx),
      });
    }
  }

  // Deduplicate by name, keeping the most recently dispensed entry
  const groups: Record<string, Medication[]> = {};
  for (const med of allMeds) {
    const key = med.name.trim().toLowerCase();
    if (!groups[key]) groups[key] = [];
    groups[key].push(med);
  }

  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  const cutoff = oneYearAgo.toISOString().slice(0, 10); // YYYY-MM-DD

  const result: Medication[] = [];
  for (const key of Object.keys(groups)) {
    const meds = groups[key];
    // Sort by dateLastDispensed descending (no-date first)
    meds.sort((a, b) => {
      if (!a.dateLastDispensed) return -1;
      if (!b.dateLastDispensed) return 1;
      return b.dateLastDispensed.localeCompare(a.dateLastDispensed);
    });
    const best = meds[0];

    // Keep if: no dispense date, dispensed within past year, or unique (only one record — never refilled)
    if (!best.dateLastDispensed || best.dateLastDispensed >= cutoff || meds.length === 1) {
      result.push(best);
    }
  }

  // Sort by last dispensed date descending (no-date entries at end)
  result.sort((a, b) => {
    if (!a.dateLastDispensed && !b.dateLastDispensed) return 0;
    if (!a.dateLastDispensed) return 1;
    if (!b.dateLastDispensed) return -1;
    return b.dateLastDispensed.localeCompare(a.dateLastDispensed);
  });

  return result;
}

function parseVaccineHistory(sheet: any): VaccineHistory[] {
  const rows = getRows(sheet);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h: any) => String(h));
  const vaccineIdx = findCol(headers, 'vaccine brand name / titer', 'vaccine', 'vaccine name');
  const dosageIdx = findCol(headers, 'dosage', 'dose');
  const dateIdx = findCol(headers, 'date');
  const providerIdx = findCol(headers, 'provider');
  const sourceIdx = findCol(headers, 'source');
  return rows.slice(1)
    .filter((r) => cellStr(r, vaccineIdx))
    .map((r) => ({
      vaccine: cellStr(r, vaccineIdx),
      dosage: cellStr(r, dosageIdx),
      date: cellStr(r, dateIdx),
      provider: cellStr(r, providerIdx),
      source: cellStr(r, sourceIdx),
    }));
}

function parseDiagnosticHistory(sheet: any): DiagnosticHistory[] {
  const rows = getRows(sheet);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h: any) => String(h));
  const procIdx = findCol(headers, 'procedure');
  const dateIdx = findCol(headers, 'date');
  const findingIdx = findCol(headers, 'key finding', 'finding');
  const notesIdx = findCol(headers, 'other notes', 'notes');
  return rows.slice(1)
    .filter((r) => cellStr(r, procIdx))
    .map((r) => ({
      procedure: cellStr(r, procIdx),
      date: cellStr(r, dateIdx),
      keyFinding: cellStr(r, findingIdx),
      notes: cellStr(r, notesIdx),
    }));
}

function parseSurgicalHistory(sheet: any): SurgicalHistory[] {
  const rows = getRows(sheet);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h: any) => String(h));
  const procIdx = findCol(headers, 'surgical procedure / reason for hospitalization', 'surgical hx & hospitalization', 'procedure', 'surgery');
  const dateIdx = findCol(headers, 'date');
  const commentsIdx = findCol(headers, 'other comments', 'comments', 'notes');
  return rows.slice(1)
    .filter((r) => cellStr(r, procIdx))
    .map((r) => ({
      procedure: cellStr(r, procIdx),
      date: cellStr(r, dateIdx),
      comments: cellStr(r, commentsIdx),
    }));
}

function parseTravelHistory(sheet: any): TravelHistory[] {
  const rows = getRows(sheet);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h: any) => String(h));
  const startIdx = findCol(headers, 'start date');
  const endIdx = findCol(headers, 'end date');
  const cityIdx = findCol(headers, 'city / region', 'city', 'region');
  const countryIdx = findCol(headers, 'country');
  return rows.slice(1)
    .filter((r) => cellStr(r, startIdx) || cellStr(r, countryIdx))
    .map((r) => ({
      startDate: cellStr(r, startIdx),
      endDate: cellStr(r, endIdx),
      cityRegion: cellStr(r, cityIdx),
      country: cellStr(r, countryIdx),
    }));
}

function parseFamilyHistory(sheet: any): FamilyHistory[] {
  const rows = getRows(sheet);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h: any) => String(h));
  const relIdx = findCol(headers, 'relation', 'relationship');
  const condIdx = findCol(headers, 'medical condition', 'condition');
  const ageIdx = findCol(headers, 'age of onset', 'age');
  const providerIdx = findCol(headers, 'provider');
  // Fill down: carry forward the last non-empty Relation for continuation rows
  let lastRelation = '';
  return rows.slice(1)
    .filter((r) => cellStr(r, condIdx))
    .map((r) => {
      const rel = cellStr(r, relIdx);
      if (rel) lastRelation = rel;
      return {
        relation: rel || lastRelation,
        condition: cellStr(r, condIdx),
        ageOfOnset: cellStr(r, ageIdx),
        provider: cellStr(r, providerIdx),
      };
    });
}

function parseCareTeam(sheet: any): CareTeamMember[] {
  const rows = getRows(sheet);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h: any) => String(h));
  const nameIdx = findCol(headers, 'name');
  const specIdx = findCol(headers, 'specialty');
  const addrIdx = findCol(headers, 'address');
  const phoneIdx = findCol(headers, 'phone');
  const visitIdx = findCol(headers, 'date of last visit', 'last visit');
  return rows.slice(1)
    .filter((r) => cellStr(r, nameIdx))
    .map((r) => ({
      name: cellStr(r, nameIdx),
      specialty: cellStr(r, specIdx),
      address: cellStr(r, addrIdx),
      phone: cellStr(r, phoneIdx),
      dateOfLastVisit: cellStr(r, visitIdx),
    }));
}

/**
 * Find a sheet by name (case-insensitive, ignoring extra whitespace).
 */
function findSheet(workbook: any, ...candidates: string[]): any | null {
  for (const name of workbook.SheetNames) {
    const normalized = name.trim().toLowerCase();
    for (const c of candidates) {
      if (normalized === c.toLowerCase()) return workbook.Sheets[name];
    }
  }
  return null;
}

export async function fetchHealthSummaryData(userId: string): Promise<HealthSummaryResult | null> {
  const docs = await listUserDocuments(userId, undefined, 'health-summary');
  const xlsxDocs = docs
    .filter((d) => d.name.toLowerCase().endsWith('.xlsx'))
    .sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());

  if (xlsxDocs.length === 0) return null;

  const url = await getDocumentUrl(xlsxDocs[0].key);
  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();

  const XLSX = await import('xlsx');
  (window as any).__XLSX__ = XLSX;

  const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array', cellDates: true });

  const personalSheet = findSheet(workbook, 'Personal Identification', 'Personal Info', 'Personal');
  const demoSheet = findSheet(workbook, 'Demographics');
  const emergencySheet = findSheet(workbook, 'Emergency Contacts', 'EmergencyContacts');
  const familyDocSheet = findSheet(workbook, 'Family Doctor');
  const conditionsSheet = findSheet(workbook, 'Active Medical Conditions', 'Conditions', 'Medical Conditions');
  const allergiesSheet = findSheet(workbook, 'Allergies & Adverse Reaction', 'Allergies');
  const medsSheet = findSheet(workbook, 'Medications', 'Medication');
  const vaccineSheet = findSheet(workbook, 'Vaccine History', 'Vaccines');
  const diagnosticSheet = findSheet(workbook, 'Diagnostic History');
  const surgicalSheet = findSheet(workbook, 'Surgical Hx & Hospitalization', 'Surgical History');
  const travelSheet = findSheet(workbook, 'Travel Hx', 'Travel History');
  const insuranceSheet = findSheet(workbook, 'Insurance & Care Planning');
  const familyHxSheet = findSheet(workbook, 'Family Hx', 'Family History');
  const careTeamSheet = findSheet(workbook, 'Care Team');

  // Extract appendix PDFs from the already-fetched docs list
  const appendixRegex = /^Appendix\s+([A-Z])/i;
  const appendices: AppendixFile[] = docs
    .filter((d) => d.name.toLowerCase().endsWith('.pdf') && appendixRegex.test(d.name))
    .map((d) => {
      const match = d.name.match(appendixRegex);
      return {
        key: d.key,
        name: d.name,
        label: match ? `Appendix ${match[1].toUpperCase()}` : d.name,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return {
    data: {
      personalIdentification: personalSheet ? parseFieldValue(personalSheet) : [],
      demographics: demoSheet ? parseFieldValue(demoSheet) : [],
      emergencyContacts: emergencySheet ? parseEmergencyContacts(emergencySheet) : [],
      familyDoctor: familyDocSheet ? parseFamilyDoctor(familyDocSheet) : [],
      conditions: conditionsSheet ? parseConditions(conditionsSheet) : [],
      allergies: allergiesSheet ? parseAllergies(allergiesSheet) : [],
      medications: medsSheet ? parseMedications(medsSheet) : [],
      vaccineHistory: vaccineSheet ? parseVaccineHistory(vaccineSheet) : [],
      diagnosticHistory: diagnosticSheet ? parseDiagnosticHistory(diagnosticSheet) : [],
      surgicalHistory: surgicalSheet ? parseSurgicalHistory(surgicalSheet) : [],
      travelHistory: travelSheet ? parseTravelHistory(travelSheet) : [],
      insuranceCarePlanning: insuranceSheet ? parseFieldValue(insuranceSheet) : [],
      familyHistory: familyHxSheet ? parseFamilyHistory(familyHxSheet) : [],
      careTeam: careTeamSheet ? parseCareTeam(careTeamSheet) : [],
    },
    uploadDate: new Date(xlsxDocs[0].lastModified),
    appendices,
  };
}
